import React, { useMemo, useState } from "react";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    Brush,
} from 'recharts';
import { useAtomValue, useSetAtom } from "jotai";
import { Box, Card, CardContent, CardHeader, Typography } from "@mui/material";
import { ChartSkeleton } from "components/skeletons/ChartSkeleton";
import { activeCabinNoiseGraphTabAtom, allConditionDataAtom, allConditionLoadingAtom } from "atoms/Rtool/cabinAtoms";
import errorImage1 from "assets/dogs/dog1.png";
import errorImage2 from "assets/dogs/dog2.png";
import { DRIVE_MODE_IDS, PHENOMENON_IDS } from "utils/consts";
import { DRIVE_MODE_IDS as HEV_DRIVE_MODE_IDS, PHENOMENON_IDS as HEV_PHENOMENON_IDS } from "utils/constsHev";
import { powertrainAtom } from "atoms/powertrainAtom";
import { gearIdAtom } from "atoms/gearAtom";
import { allNullData, allStatusError } from "utils/utils";
import { makeTicksFromData } from "utils/charts/AxisLabel";
import { conditionIdAtom } from "atoms/Rtool/stageConfigAtoms";
import { mappingAtom } from "atoms/mappingAtoms";
import { getMappingLabel } from "utils/mappingLabel";
import { ChartZoomResetButton } from "components/charts/ChartZoomResetButton";
import { YAxisRangeSlider } from "components/charts/YAxisRangeSlider";
import { CROSSHAIR_CURSOR } from "utils/charts/chartConstants";
import { computeSharedYBounds } from "utils/charts/sharedYDomain";
import { resolveCellYAxisRange, useSharedYAxisRange } from "utils/charts/useSharedYAxisRange";
import { useAllConditionDataSetter } from "../utils/useAllConditionDataSetter";
import { buildConditionIds } from "../utils/utils";

export const AllConditionChart: React.FC = () => {
    const powertrain = useAtomValue(powertrainAtom);
    const gearId = useAtomValue(gearIdAtom);
    const isHev = powertrain === 'hev';
    const phenomenonIds: readonly string[] = isHev ? HEV_PHENOMENON_IDS : PHENOMENON_IDS;
    const driveModeIds: readonly string[] = isHev ? HEV_DRIVE_MODE_IDS : DRIVE_MODE_IDS;

    const conditionIds = buildConditionIds(isHev);
    useAllConditionDataSetter(conditionIds);

    const allConditionData = useAtomValue(allConditionDataAtom);
    const isLoading = useAtomValue(allConditionLoadingAtom);
    const setActiveTab = useSetAtom(activeCabinNoiseGraphTabAtom);
    const setConditionId = useSetAtom(conditionIdAtom);
    const mapping = useAtomValue(mappingAtom);
    // B1: Brush ズームのリセット用。値を変えると各 LineChart を再マウントして全域へ戻す（表示のみ）。
    const [zoomKey, setZoomKey] = useState(0);
    // 描画中セルを横断して縦軸を統一する。集計は実際に <Line> で描画している系列のみ
    // （vel / rpm は Y 系列ではないため対象外）。
    const sharedYBounds = useMemo(
        () =>
            computeSharedYBounds(
                Object.values(allConditionData)
                    .filter((entry) => entry?.status === "success")
                    .flatMap((entry) => [
                        entry.data?.cabin_noise_target,
                        entry.data?.cabin_noise_pred,
                    ])
            ),
        [allConditionData]
    );
    const sharedY = useSharedYAxisRange(sharedYBounds);

    // HEV は gear 単位で描画する。gear 未選択時は選択を促す。
    if (isHev && !gearId) {
        return (
            <Box display="flex" flexDirection="column" alignItems="center" mt={4}>
                <img src={errorImage1} alt="Gear 未選択" height={400} />
                <Typography align="center" variant="body1">HEV では Gear を選択してください。</Typography>
            </Box>
        );
    }

    const status =
        Object.keys(allConditionData).length === 0
            ? { img: errorImage1, msg: "計算セットが選択されていません。" }
            : allStatusError(allConditionData)
                ? { img: errorImage2, msg: "指定された条件に合致するデータがありません。" }
                : allNullData(allConditionData)
                    ? { img: errorImage2, msg: "全てのデータが空です。" }
                    : null;

    if (isLoading) {
        return <ChartSkeleton height={300} />;
    }

    if (status) {
        return (
            <Box display="flex" flexDirection="column" alignItems="center" mt={4}>
                <img src={status.img} alt="Error" height={400} />
                <Typography align="center" variant="body1">{status.msg}</Typography>
            </Box>
        );
    }

    const handleCardClick = (conditionId: string | null) => {
        setConditionId(conditionId);
        setActiveTab(1);
    };

    return (
        <>
        {/* 縦軸コントロールは見出し直下の左側、ズームリセットは右端に置く */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, px: 1, pt: 1 }}>
            <YAxisRangeSlider
                bounds={sharedY.bounds}
                range={sharedY.range}
                onChange={sharedY.setRange}
                step={sharedY.step}
                unified={sharedY.unified}
                onUnifiedChange={sharedY.setUnified}
            />
            {/* x（Brush 再マウント）と y（選択範囲）の両方を全域へ戻す */}
            <ChartZoomResetButton
                onClick={() => {
                    setZoomKey((k) => k + 1);
                    sharedY.reset();
                }}
            />
        </Box>
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: `150px repeat(${phenomenonIds.length}, minmax(320px, 1fr))`,
                gap: 1,
                overflowX: "auto",
                p: 1,
            }}
        >
            {/* ヘッダー行の先頭セル */}
            <Box />

            {/* 列ヘッダー（Phenomenon） */}
            {phenomenonIds.map((ph) => (
                <Box
                    key={ph ?? "null"}
                    sx={{ fontWeight: "bold", textAlign: "center", alignSelf: "center" }}
                >
                    {ph ? getMappingLabel(mapping, 'phenomenon_id', ph) : "その他"}
                </Box>
            ))}

            {/* 各行（Drive Mode） */}
            {driveModeIds.map((dm) => (
                <React.Fragment key={dm ?? "null"}>
                    {/* 行ヘッダー */}
                    <Box
                        sx={{
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                            pl: 1,
                        }}
                    >
                        {dm ? getMappingLabel(mapping, 'drive_mode_id', dm) : "その他"}
                    </Box>

                    {/* 各セル */}
                    {phenomenonIds.map((ph) => {
                        const conditionId = `${ph}-${dm}`;
                        const entry = allConditionData[conditionId];

                        return (
                            <Card
                                key={conditionId ?? "null"}
                                onClick={(e) => {
                                    const target = e.target as HTMLElement | null;
                                    if (target && target.closest(".recharts-brush")) {
                                        return;
                                    }
                                    handleCardClick(conditionId ?? null);
                                }}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    minWidth: 320,
                                    flexShrink: 0,
                                    height: 260,      // 必要に応じて高さを調整
                                }}
                            >
                                <CardHeader />
                                <CardContent sx={{
                                    p: 0,
                                    flex: 1,              // ここで残り高さを全部使う
                                    display: 'flex',
                                    justifyContent: 'center',   // 垂直方向の中央揃え
                                    alignItems: 'center',
                                }}>
                                    {!entry ? (
                                        <Typography variant="body2" align="center">
                                            データなし
                                        </Typography>
                                    ) : entry.status === "loading" ||
                                        entry.status === "notstarted" ? (
                                        <Typography variant="body2" align="center">
                                            読み込み中...
                                        </Typography>
                                    ) : entry.status === "error" ? (
                                        <Typography variant="body2" align="center" color="error">
                                            データ読み込み中にエラーが発生しました。
                                        </Typography>
                                    ) : (
                                        (() => {
                                            const indexScale = ph.split("_")[1] === "aco" ? 10 : 1;
                                            const target = entry.data.cabin_noise_target ?? [];
                                            const pred = entry.data.cabin_noise_pred ?? [];
                                            const maxLength = Math.max(
                                                target.length,
                                                pred.length
                                            );
                                            const chartData = Array.from({ length: maxLength }, (_, i) => ({
                                                index: (i + 1) * indexScale,
                                                target: target[i] ?? null,
                                                pred: pred[i] ?? null,
                                            }));
                                            const ticks = makeTicksFromData(chartData, "index", 250 * indexScale);
                                            const cellY = resolveCellYAxisRange([target, pred], sharedY);

                                            return (
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <LineChart
                                                        key={`${conditionId}-${zoomKey}`}
                                                        margin={{ right: 20, left: -20 }}
                                                        data={chartData}
                                                    >
                                                        <XAxis
                                                            dataKey="index"
                                                            xAxisId="0"
                                                            height={18}
                                                            ticks={ticks}
                                                            tick={{ fontSize: 8 }}
                                                            label={{ value: "Frequency", position: "insideLeft", offset: -20, fontSize: 8 }}
                                                        />
                                                        <YAxis
                                                            tick={{ fontSize: 10 }}
                                                            domain={cellY.domain}
                                                            ticks={cellY.ticks}
                                                            allowDataOverflow={cellY.domain !== undefined}
                                                        />
                                                        <Tooltip
                                                            cursor={CROSSHAIR_CURSOR}
                                                            formatter={(value: any, name: string) => {
                                                                if (typeof value === "number") {
                                                                    return [value.toFixed(3), name];
                                                                }
                                                                return ["-", name];
                                                            }}
                                                            labelFormatter={(label) => `周波数: ${label}`}
                                                            contentStyle={{ fontSize: 10, padding: "4px 8px" }}
                                                            labelStyle={{ fontSize: 10, marginBottom: 2 }}
                                                            itemStyle={{ fontSize: 10, padding: 0 }}
                                                            offset={30}
                                                            wrapperStyle={{ pointerEvents: "none" }}
                                                        />
                                                        <Line
                                                            type="monotone"
                                                            dataKey="target"
                                                            name="室内音目標値"
                                                            stroke="#ff2c95ff"
                                                            dot={false}
                                                        />
                                                        <Line
                                                            type="monotone"
                                                            dataKey="pred"
                                                            name="室内音計算値"
                                                            stroke="#006affff"
                                                            dot={false}
                                                        />
                                                        <Brush
                                                            dataKey="index"
                                                            height={15}
                                                            travellerWidth={5}
                                                            startIndex={0}
                                                            endIndex={chartData.length - 1}
                                                            onClick={(e: any) => e.stopPropagation()}
                                                            onMouseDown={(e: any) => e.stopPropagation()}
                                                            onMouseUp={(e: any) => e.stopPropagation()}
                                                        />
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            );
                                        })()
                                    )}
                                </CardContent>
                            </Card>
                        );
                    })}
                </React.Fragment>
            ))
            }
        </Box >
        </>
    );
};
